import { useMemo, useState, useEffect } from 'react'
import { useParams, useNavigate, useMatchRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { VisibilityBadge } from './VisibilityBadge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { AppIcon } from './ui/icon'
import { mdiAccount, mdiAlertCircleOutline, mdiArrowLeft, mdiCalendar, mdiClose, mdiEmailFastOutline, mdiGraphOutline, mdiPencil, mdiTrashCanOutline  } from '@mdi/js'
import {
  useIntervention,
  useInterventionDocuments,
  useInterventionEvents,
  useUpdateIntervention,
  useDeleteIntervention,
  type InterventionKind,
  type InterventionStatus,
} from '../hooks/useInterventions'
import { useAsset, useUser, useContract, useContracts } from '../hooks/useQueries'
import { useInterventionCoverage } from './assemblies/assembly-api'
import { CoverageSummary } from './assemblies/CoverageSummary'
import { UserSelect } from './UserSelect'
import type { ApiSearchableSelectOption } from './ApiSearchableSelect'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { KIND_VARIANT, STATUS_VARIANT, KINDS, STATUSES, formatCost } from './InterventionsPage'
import { InterventionDocumentsSection } from './interventions/InterventionDocumentsSection'
import { InterventionTimelineSection } from './interventions/InterventionTimelineSection'
import { InterventionRelatedExpenses } from './InterventionRelatedExpenses'
import { WorkOrderChips } from './workorders/WorkOrderChips'
import { EmailDeliveryChip, type EmailDelivery } from './workorders/EmailDeliveryChip'
import { InterventionValidationSection } from './workorders/InterventionValidationSection'
import { SendWorkOrderDialog } from './workorders/SendWorkOrderDialog'
import { SignatureRequestsPanel } from './signatures/SignatureRequestsPanel'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { SECTION_ICONS } from '../lib/section-icons'

function toISO(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

function toLocalInput(iso?: string | null): string {
  return iso ? iso.slice(0, 16) : ''
}

function InterventionCoverage({ org, interventionUid }: { org: string; interventionUid: string }) {
  const { data } = useInterventionCoverage(org, interventionUid)
  return <CoverageSummary coverage={data as never} testId="intervention-coverage" />
}

export function InterventionPage() {
  const { org, interventionUid } = useParams({ from: '/$org/interventions/$interventionUid' })
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()
  const { t } = useTranslation('interventions')
  const { locale } = useLocale()

  const isEditing = !!matchRoute({ to: '/$org/interventions/$interventionUid/edit' })

  const { data: iv, isLoading, error } = useIntervention(org || 'demo', interventionUid || '')
  const { data: documents = [] } = useInterventionDocuments(org || 'demo', interventionUid)
  const { data: events = [] } = useInterventionEvents(org || 'demo', interventionUid)

  const { data: asset } = useAsset(org || 'demo', iv?.asset_uid || '')
  const { data: provider } = useUser(org || 'demo', iv?.provider_user_uid || '', { enabled: !!iv?.provider_user_uid })
  const { data: contract } = useContract(org || 'demo', iv?.contract_uid || '')
  const { data: contracts = [] } = useContracts(org || 'demo', { limit: 200 })

  const updateMutation = useUpdateIntervention(org || 'demo', iv?.asset_uid)
  const deleteMutation = useDeleteIntervention(org || 'demo', iv?.asset_uid)

  // Form state (edit mode)
  const [sendWorkOrderOpen, setSendWorkOrderOpen] = useState(false)
  const [kind, setKind] = useState<InterventionKind>('corrective')
  const [category, setCategory] = useState('')
  const [status, setStatus] = useState<InterventionStatus>('requested')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [performedAt, setPerformedAt] = useState('')
  const [costEur, setCostEur] = useState('')
  const [costCurrency, setCostCurrency] = useState('EUR')
  const [providerUserUid, setProviderUserUid] = useState('')
  const [contractUid, setContractUid] = useState('')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  // Captured at selection time (UserSelect's onOptionSelect) so the combobox
  // shows the NEWLY picked provider's name immediately, instead of the stale
  // label from the original useUser(iv.provider_user_uid) fetch — which only
  // updates after save+refetch. See
  // specs/todos/2026-07-21-04-user-pickers-truncated-to-first-page.md.
  const [selectedProviderOption, setSelectedProviderOption] = useState<ApiSearchableSelectOption | null>(null)

  useEffect(() => {
    if (iv) {
      setKind(iv.kind)
      setCategory(iv.category || '')
      setStatus(iv.status)
      setTitle(iv.title || '')
      setDescription(iv.description || '')
      setScheduledAt(toLocalInput(iv.scheduled_at))
      setPerformedAt(toLocalInput(iv.performed_at))
      setCostEur(iv.cost_amount != null ? String(iv.cost_amount / 100) : '')
      setCostCurrency(iv.cost_currency || 'EUR')
      setProviderUserUid(iv.provider_user_uid || '')
      setContractUid(iv.contract_uid || '')
      setSelectedProviderOption(null)
    }
  }, [iv])

  // ApiSearchableSelect needs an option matching `value` to render the
  // current provider's label instead of a placeholder while its own list is
  // unloaded/filtered. Prefer the label captured at selection time (correct
  // even for a just-picked provider the page didn't load with); fall back to
  // the provider already fetched via useUser for the initial mount value.
  const providerInitialOptions = useMemo<ApiSearchableSelectOption[]>(() => {
    if (selectedProviderOption && selectedProviderOption.value && selectedProviderOption.value === providerUserUid) {
      return [selectedProviderOption]
    }
    if (providerUserUid && provider) {
      return [{ value: providerUserUid, label: provider.name || provider.email || providerUserUid }]
    }
    return []
  }, [providerUserUid, provider, selectedProviderOption])

  const handleEdit = () => {
    navigate({ to: '/$org/interventions/$interventionUid/edit', params: { org: org || 'demo', interventionUid: interventionUid || '' } })
  }

  const handleCancel = () => {
    navigate({ to: '/$org/interventions/$interventionUid', params: { org: org || 'demo', interventionUid: interventionUid || '' } })
  }

  const handleSave = async () => {
    if (!iv) return
    try {
      const cost = costEur ? Math.round(parseFloat(costEur) * 100) : null
      await updateMutation.mutateAsync({
        uid: iv.uid,
        kind,
        category: category.trim() || undefined,
        status,
        title: title.trim(),
        description: description.trim(),
        scheduled_at: toISO(scheduledAt),
        performed_at: toISO(performedAt),
        cost_amount: cost,
        cost_currency: cost != null ? costCurrency : null,
        provider_user_uid: providerUserUid || null,
        contract_uid: contractUid || null,
      })
      navigate({ to: '/$org/interventions/$interventionUid', params: { org: org || 'demo', interventionUid: interventionUid || '' } })
    } catch (err) {
      console.error('Failed to update intervention:', err)
    }
  }

  const confirmDelete = async () => {
    if (!iv) return
    try {
      await deleteMutation.mutateAsync(iv.uid)
      setShowDeleteDialog(false)
      window.location.href = `/${org}/interventions`
    } catch (err) {
      console.error('Failed to delete intervention:', err)
    }
  }

  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/interventions` },
    {
      title: iv
        ? (isEditing ? t('detail.breadcrumb_editing', { title: iv.title }) : iv.title)
        : t('detail.breadcrumb_details'),
      path: isEditing ? undefined : `/${org}/interventions/${interventionUid}`,
    },
  ], [org, iv, isEditing, interventionUid, t])
  useBreadcrumbs(breadcrumbs)

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  if (error || !iv) {
    return (
      <div className="container mx-auto py-8 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">{t('detail.error_title')}</h1>
          <p className="text-muted-foreground">
            {(error as any)?.title || (error as any)?.message || t('detail.error_not_found')}
          </p>
          <Link to="/$org/interventions" params={{ org: org || 'demo' }} search={{ kind: undefined, status: undefined }}>
            <Button className="mt-4" data-testid="intervention-back-to-list">
              <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
              {t('detail.back_to_interventions')}
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  // Delivery state of the ordre de service e-mail, written by the backend on
  // properties.os_email (queued at send time, sent/failed from the email job).
  const osEmail = (iv.properties?.os_email as EmailDelivery | undefined) ?? undefined

  return (
    <div className="container mx-auto py-8 max-w-6xl" data-testid="intervention-detail">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <AppIcon tone="muted" path={SECTION_ICONS.interventions} size="xl" />
              <h1 className="text-3xl font-bold text-foreground" data-testid="intervention-title">
                {isEditing ? `${t('detail.editing_prefix')} ${iv.title}` : iv.title}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
              <Badge variant={KIND_VARIANT[iv.kind]}>{t(`kind.${iv.kind}`, { defaultValue: iv.kind })}</Badge>
              <Badge variant={STATUS_VARIANT[iv.status]}>{t(`status.${iv.status}`, { defaultValue: iv.status })}</Badge>
              {/* Audience (spec 2026-09-04-03, decision 7). The same badge the
                  documents and issues carry; only the toggle is gated on
                  `interventions.write` on THIS row. */}
              <VisibilityBadge
                visibility={iv.visibility ?? 'internal'}
                writePermission="interventions.write"
                entity={iv}
                onChange={(next) => {
                  void updateMutation.mutateAsync({ uid: iv.uid, visibility: next })
                }}
                testId="intervention-visibility-badge"
              />
              <span className="font-mono text-xs text-muted-foreground">{iv.reference}</span>
            </div>
            {/* Work-order state (spec 2026-08-01-01): did they answer, and when. */}
            <div className="mt-2">
              <WorkOrderChips intervention={iv} />
            </div>
            {/* Did the OS e-mail actually leave? (spec 2026-08-03-01 §3) — the
                os_sent chip above only says we tried. */}
            {osEmail && (
              <div className="mt-2">
                <EmailDeliveryChip org={org || 'demo'} delivery={osEmail} />
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {!isEditing && iv?.uid && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSendWorkOrderOpen(true)}
                data-testid="send-work-order-btn"
                aria-label={t('workorders:send.button')}
                title={t('workorders:send.button')}
              >
                <AppIcon path={mdiEmailFastOutline} size="sm" className="md:mr-1" />
                <span className="hidden md:inline">{t('workorders:send.button')}</span>
              </Button>
            )}
            {iv?.uid && (
              <Link
                to="/$org/graph"
                params={{ org: org || 'demo' }}
                search={{ focus: `iv@${iv.uid}`, expanded: undefined }}
              >
                <Button
                  variant="outline"
                  size="icon"
                  data-testid="view-in-graph-btn"
                  aria-label={t('graph:action.view_in_graph')}
                  title={t('graph:action.view_in_graph')}
                >
                  <AppIcon path={mdiGraphOutline} size="sm" />
                </Button>
              </Link>
            )}
            {!isEditing ? (
              <Button
                variant="outline"
                size="icon"
                onClick={handleEdit}
                data-testid="edit-intervention"
                aria-label={t('detail.edit')}
                title={t('detail.edit')}
              >
                <AppIcon path={mdiPencil} size="sm" />
              </Button>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  data-testid="save-intervention"
                  aria-label={t('detail.save')}
                  title={t('detail.save')}
                >
                  <AppIcon path={mdiPencil} size="sm" className="md:mr-2" />
                  <span className="hidden md:inline">{t('detail.save')}</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  data-testid="cancel-edit-intervention"
                  aria-label={t('detail.cancel')}
                  title={t('detail.cancel')}
                >
                  <AppIcon path={mdiClose} size="sm" className="md:mr-2" />
                  <span className="hidden md:inline">{t('detail.cancel')}</span>
                </Button>
              </div>
            )}
            <Button
              variant="destructive"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleteMutation.isPending}
              data-testid="delete-intervention"
              aria-label={t('detail.delete')}
              title={t('detail.delete')}
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          </div>
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AppIcon path={SECTION_ICONS.documents} size="md" />
                  {t('detail.section_details')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Title */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_title')}</h3>
                    {isEditing ? (
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        placeholder={t('detail.field_title_placeholder')}
                        data-testid="intervention-field-title"
                        autoFocus
                      />
                    ) : (
                      <p className="text-muted-foreground">{iv.title}</p>
                    )}
                  </div>

                  {/* Kind */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_kind')}</h3>
                    {isEditing ? (
                      <select
                        value={kind}
                        onChange={(e) => setKind(e.target.value as InterventionKind)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="intervention-field-kind"
                      >
                        {KINDS.map((k) => (
                          <option key={k} value={k}>{t(`kind.${k}`)}</option>
                        ))}
                      </select>
                    ) : (
                      <Badge variant={KIND_VARIANT[iv.kind]}>{t(`kind.${iv.kind}`, { defaultValue: iv.kind })}</Badge>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_status')}</h3>
                    {isEditing ? (
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as InterventionStatus)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="intervention-field-status"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{t(`status.${s}`)}</option>
                        ))}
                      </select>
                    ) : (
                      <Badge variant={STATUS_VARIANT[iv.status]}>{t(`status.${iv.status}`, { defaultValue: iv.status })}</Badge>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_category')}</h3>
                    {isEditing ? (
                      <input
                        type="text"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        placeholder={t('detail.field_category_placeholder')}
                        data-testid="intervention-field-category"
                      />
                    ) : (
                      <p className="text-muted-foreground">{iv.category || t('detail.no_value')}</p>
                    )}
                  </div>

                  {/* Scheduled at */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <AppIcon path={mdiCalendar} size="sm" />
                      {t('detail.field_scheduled_at')}
                    </h3>
                    {isEditing ? (
                      <input
                        type="datetime-local"
                        value={scheduledAt}
                        onChange={(e) => setScheduledAt(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="intervention-field-scheduled-at"
                      />
                    ) : (
                      <p className="text-muted-foreground">{iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString(locale) : t('detail.no_value')}</p>
                    )}
                  </div>

                  {/* Performed at */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <AppIcon path={mdiCalendar} size="sm" />
                      {t('detail.field_performed_at')}
                    </h3>
                    {isEditing ? (
                      <input
                        type="datetime-local"
                        value={performedAt}
                        onChange={(e) => setPerformedAt(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="intervention-field-performed-at"
                      />
                    ) : (
                      <p className="text-muted-foreground">{iv.performed_at ? new Date(iv.performed_at).toLocaleString(locale) : t('detail.no_value')}</p>
                    )}
                  </div>

                  {/* Cost */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_cost')}</h3>
                    {isEditing ? (
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={costEur}
                          onChange={(e) => setCostEur(e.target.value)}
                          className="mt-1 w-full px-3 py-2 border rounded-md"
                          placeholder={t('detail.field_cost_placeholder')}
                          data-testid="intervention-field-cost"
                        />
                        <input
                          type="text"
                          value={costCurrency}
                          onChange={(e) => setCostCurrency(e.target.value)}
                          className="mt-1 w-20 px-3 py-2 border rounded-md"
                          data-testid="intervention-field-cost-currency"
                        />
                      </div>
                    ) : (
                      <p className="text-muted-foreground">{formatCost(iv.cost_amount, iv.cost_currency)}</p>
                    )}
                  </div>

                  {/* Provider */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_provider')}</h3>
                    {isEditing ? (
                      <UserSelect
                        org={org || 'demo'}
                        value={providerUserUid}
                        onValueChange={setProviderUserUid}
                        onOptionSelect={setSelectedProviderOption}
                        initialOptions={providerInitialOptions}
                        placeholder={t('detail.field_provider_none')}
                        data-testid="intervention-field-provider"
                      />
                    ) : (
                      <p className="text-muted-foreground">{provider?.name || provider?.email || t('detail.provider_unknown')}</p>
                    )}
                  </div>

                  {/* Contract */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_contract')}</h3>
                    {isEditing ? (
                      <select
                        value={contractUid}
                        onChange={(e) => setContractUid(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-md"
                        data-testid="intervention-field-contract"
                      >
                        <option value="">{t('detail.field_contract_none')}</option>
                        {contracts.map((c: any) => (
                          <option key={c.uid} value={c.uid}>{c.name || c.code || c.uid}</option>
                        ))}
                      </select>
                    ) : contract ? (
                      <Link
                        to="/$org/contracts/$contractUid"
                        params={{ org: org || 'demo', contractUid: contract.uid! }}
                        className="text-primary hover:underline"
                        data-testid="intervention-contract-link"
                      >
                        {(contract as any).name || (contract as any).code}
                      </Link>
                    ) : (
                      <p className="text-muted-foreground">{t('detail.contract_unknown')}</p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold text-foreground mb-2">{t('detail.field_description')}</h3>
                  {isEditing ? (
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="mt-1 w-full px-3 py-2 border rounded-md"
                      placeholder={t('detail.field_description_placeholder')}
                      data-testid="intervention-field-description"
                    />
                  ) : (
                    <p className="text-muted-foreground leading-relaxed">
                      {iv.description || t('detail.field_description_empty')}
                    </p>
                  )}
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-border">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('detail.field_created')}</p>
                    <p className="text-sm">{iv.created_at ? new Date(iv.created_at).toLocaleString(locale) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('detail.field_updated')}</p>
                    <p className="text-sm">{iv.updated_at ? new Date(iv.updated_at).toLocaleString(locale) : '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {!isEditing && (
              <Card>
                <CardContent className="pt-6 space-y-6">
                  <InterventionValidationSection
                    org={org || 'demo'}
                    interventionUid={iv.uid}
                    workSubmittedAt={iv.work_submitted_at}
                  />
                  <InterventionDocumentsSection org={org || 'demo'} assetUid={iv.asset_uid} interventionUid={iv.uid} documents={documents} />
                  <InterventionTimelineSection events={events} showSeparator={false} />
                </CardContent>
              </Card>
            )}
            {/* Signature requests carried by this intervention (spec 2026-10-05-05) */}
            {!isEditing && iv.uid && (
              <SignatureRequestsPanel
                org={org || 'demo'}
                filter={{ subject_type: 'work_order', subject_uid: iv.uid }}
                testId="intervention-signature-requests"
              />
            )}
          </div>

          {/* Sidebar: relations */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('detail.section_relations')}</CardTitle>
                <CardDescription>
                  {isEditing && (
                    <span className="flex items-center gap-1 text-xs" data-testid="intervention-relations-not-editable">
                      <AppIcon path={mdiAlertCircleOutline} size="xs" />
                      {t('detail.asset_not_editable')}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="intervention-relations">
                  {/* Asset — immutable via PATCH, always a link */}
                  <div className="flex items-start gap-3">
                    <AppIcon path={SECTION_ICONS.assets} size="sm" className="mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">{t('detail.related_asset')}</p>
                      <Link
                        to="/$org/assets/$assetUid"
                        params={{ org: org || 'demo', assetUid: iv.asset_uid }}
                        className="text-sm font-medium text-primary hover:underline truncate block"
                        data-testid="intervention-asset-link"
                      >
                        {(asset as any)?.name || t('detail.asset_unknown')}
                      </Link>
                    </div>
                  </div>

                  {/* Contract */}
                  {iv.contract_uid && !isEditing && (
                    <div className="flex items-start gap-3">
                      <AppIcon path={SECTION_ICONS.contracts} size="sm" className="mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">{t('detail.related_contract')}</p>
                        <Link
                          to="/$org/contracts/$contractUid"
                          params={{ org: org || 'demo', contractUid: iv.contract_uid }}
                          className="text-sm font-medium text-primary hover:underline truncate block"
                          data-testid="intervention-contract-relation-link"
                        >
                          {(contract as any)?.name || (contract as any)?.code || t('detail.contract_unknown')}
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Which assembly resolution covers the spending (spec 2026-10-05-02) */}
                  {iv.contract_uid && !isEditing && (
                    <InterventionCoverage org={org || 'demo'} interventionUid={iv.uid || ''} />
                  )}

                  {/* Provider */}
                  {iv.provider_user_uid && !isEditing && (
                    <div className="flex items-start gap-3">
                      <AppIcon path={mdiAccount} size="sm" className="mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">{t('detail.related_provider')}</p>
                        <Link
                          to="/$org/users/$userUid"
                          params={{ org: org || 'demo', userUid: iv.provider_user_uid }}
                          className="text-sm font-medium text-primary hover:underline truncate block"
                          data-testid="intervention-provider-link"
                        >
                          {provider?.name || provider?.email || t('detail.provider_unknown')}
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Issue — read-only: the backend update endpoint does not
                      accept issue_uid, so this link cannot be edited here. */}
                  {iv.issue_uid && (
                    <div className="flex items-start gap-3">
                      <AppIcon path={mdiAlertCircleOutline} size="sm" className="mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">{t('detail.related_issue')}</p>
                        <Link
                          to="/$org/issues/$uid"
                          params={{ org: org || 'demo', uid: iv.issue_uid }}
                          className="text-sm font-medium text-primary hover:underline truncate block"
                          data-testid="intervention-issue-link"
                        >
                          {t('detail.view_issue')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {!isEditing && (
          <InterventionRelatedExpenses org={org || 'demo'} interventionUid={iv.uid} />
        )}
      </div>

      {/* Delete confirmation — AlertDialog (never native confirm()). */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-intervention-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.delete_confirm_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-intervention-cancel">{t('detail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="delete-intervention-confirm"
            >
              {t('detail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Draft-and-confirm send of the ordre de service (spec 2026-08-01-01 §4). */}
      <SendWorkOrderDialog
        org={org || 'demo'}
        interventionUid={iv.uid}
        interventionTitle={iv.title}
        providerEmail={provider?.email}
        open={sendWorkOrderOpen}
        onOpenChange={setSendWorkOrderOpen}
      />
    </div>
  )
}

export default InterventionPage
